import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { memo, useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@eruvaka/web-pondlogs-i18n';
import { colorTokens } from '@eruvaka/web-pondlogs-base';
const DEFAULT_COLS = [
    { key: '__area', labelKey: 'table.size', w: 80, align: 'right' },
    { key: '__pls', labelKey: 'table.plsStocked', w: 90, align: 'right' },
    { key: '__density', labelKey: 'table.density', w: 100, align: 'right' },
    { key: '__doc', labelKey: 'table.doc', w: 60, align: 'right' },
];
const CAT_EXTRA = {
    Culture: [{ key: '__shrimps', labelKey: 'table.shrimpsPerPm', w: 90, align: 'right' }],
    Devices: [
        { key: '__pls', labelKey: 'table.colPmCount', w: 80, align: 'right' },
        { key: '__density', labelKey: 'table.colMode', w: 80 },
    ],
    Growth: [
        { key: '__shrimps', labelKey: 'table.colAbw', w: 72, align: 'right' },
        { key: '__doc', labelKey: 'table.colBiomass', w: 90, align: 'right' },
        { key: '__density', labelKey: 'table.colSurvival', w: 80, align: 'right' },
        { key: '__area', labelKey: 'table.colFcr', w: 60, align: 'right' },
    ],
    Feeding: [
        { key: '__shrimps', labelKey: 'table.colTotalFeed', w: 72, align: 'right' },
        { key: '__doc', labelKey: 'table.colDailyFeed', w: 72, align: 'right' },
        { key: '__density', labelKey: 'table.colFeedLevel', w: 80, align: 'right' },
    ],
    'Water Quality': [
        { key: '__shrimps', labelKey: 'table.colDo', w: 80, align: 'right' },
        { key: '__doc', labelKey: 'table.colTemp', w: 80, align: 'right' },
    ],
    Alerts: [
        { key: '__shrimps', labelKey: 'table.colAlerts', w: 60, align: 'right' },
        { key: '__doc', labelKey: 'table.colDoShort', w: 60, align: 'right' },
        { key: '__density', labelKey: 'table.colTempShort', w: 60, align: 'right' },
    ],
};
const NAME_W = 144;
const ACTIONS_W = 96;
const PAGE_SIZES = [10, 25, 50];
const AVG_KEYS = new Set(['__density', '__doc']);
const SUM_KEYS = new Set(['__area', '__pls', '__shrimps']);
function getCols(category, lifecycle) {
    if (lifecycle !== 'Active')
        return DEFAULT_COLS;
    return [...DEFAULT_COLS, ...CAT_EXTRA[category]];
}
function getCellValue(key, pond) {
    switch (key) {
        case '__area':
            return pond.areaHectares != null ? pond.areaHectares.toFixed(2) : '—';
        case '__pls':
            return pond.plStocked != null ? pond.plStocked.toLocaleString() : '—';
        case '__density':
            return pond.densityPerSqm != null ? pond.densityPerSqm.toFixed(1) : '—';
        case '__doc':
            return pond.doc != null && pond.doc > 0 ? String(pond.doc) : '—';
        case '__shrimps':
            return pond.shrimpsPerPm != null ? String(pond.shrimpsPerPm) : '—';
        default:
            return '—';
    }
}
function getNumericValue(key, pond) {
    switch (key) {
        case '__area':
            return pond.areaHectares ?? null;
        case '__pls':
            return pond.plStocked ?? null;
        case '__density':
            return pond.densityPerSqm ?? null;
        case '__doc':
            return pond.doc ?? null;
        case '__shrimps':
            return pond.shrimpsPerPm ?? null;
        default:
            return null;
    }
}
function formatTotal(key, value) {
    switch (key) {
        case '__area':
            return value.toFixed(2);
        case '__density':
            return value.toFixed(1);
        case '__doc':
            return Math.round(value).toString();
        case '__pls':
            return value >= 1_000 ? `${(value / 1_000).toFixed(0)}K` : String(Math.round(value));
        case '__shrimps':
            return `${(value / 1_000).toFixed(0)}K`;
        default:
            return Math.round(value).toLocaleString();
    }
}
/* ── Chevron icons (inline SVG) ── */
const ChevL = () => (_jsx("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", children: _jsx("path", { d: "M8 2L4 6L8 10", stroke: colorTokens.darkGrey, strokeWidth: "1.5", strokeLinecap: "round" }) }));
const ChevR = () => (_jsx("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", children: _jsx("path", { d: "M4 2L8 6L4 10", stroke: colorTokens.darkGrey, strokeWidth: "1.5", strokeLinecap: "round" }) }));
export const PondTableView = memo((props) => {
    const { t } = useTranslation('ponds');
    const { ponds, category = 'Culture', lifecycle = 'Active', page: controlledPage, perPage: controlledPerPage, totalItems, totalPages: controlledTotalPages, onPerPageChange, onEdit, onDelete, onActivate, onRestore, onPageChange, } = props;
    const cols = useMemo(() => getCols(category, lifecycle), [category, lifecycle]);
    const [localPage, setLocalPage] = useState(1);
    const [localPerPage, setLocalPerPage] = useState(25);
    const isControlledPagination = typeof controlledPage === 'number' &&
        typeof controlledPerPage === 'number' &&
        typeof controlledTotalPages === 'number' &&
        typeof onPageChange === 'function' &&
        typeof onPerPageChange === 'function';
    const page = isControlledPagination ? controlledPage : localPage;
    const perPage = isControlledPagination ? controlledPerPage : localPerPage;
    const setPageSafe = (nextPage) => {
        if (isControlledPagination) {
            onPageChange(nextPage);
            return;
        }
        setLocalPage(nextPage);
    };
    const setPerPageSafe = (nextPerPage) => {
        if (isControlledPagination) {
            onPerPageChange(nextPerPage);
            return;
        }
        setLocalPerPage(nextPerPage);
    };
    useEffect(() => {
        if (!isControlledPagination) {
            setLocalPage(1);
        }
    }, [ponds, perPage, category, lifecycle, isControlledPagination]);
    const totalPages = isControlledPagination
        ? Math.max(1, controlledTotalPages ?? 1)
        : Math.max(1, Math.ceil(ponds.length / perPage));
    const pageRows = isControlledPagination
        ? ponds
        : ponds.slice((page - 1) * perPage, page * perPage);
    const totalCount = isControlledPagination ? (totalItems ?? ponds.length) : ponds.length;
    const totalW = NAME_W + cols.reduce((s, c) => s + c.w, 0) + ACTIONS_W;
    /* ── Column totals ── */
    const colTotals = useMemo(() => {
        const result = {};
        cols.forEach((c) => {
            if (!SUM_KEYS.has(c.key) && !AVG_KEYS.has(c.key)) {
                result[c.key] = null;
                return;
            }
            const vals = ponds
                .map((p) => getNumericValue(c.key, p))
                .filter((v) => v !== null);
            if (vals.length === 0) {
                result[c.key] = null;
                return;
            }
            const isAvg = AVG_KEYS.has(c.key);
            result[c.key] = {
                value: isAvg
                    ? vals.reduce((a, b) => a + b, 0) / vals.length
                    : vals.reduce((a, b) => a + b, 0),
                isAvg,
            };
        });
        return result;
    }, [ponds, cols]);
    /* ── Page number pills ── */
    const pageNums = useMemo(() => {
        const nums = [];
        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || Math.abs(i - page) <= 1) {
                if (nums.length > 0 &&
                    typeof nums[nums.length - 1] === 'number' &&
                    i - nums[nums.length - 1] > 1) {
                    nums.push('…');
                }
                nums.push(i);
            }
        }
        return nums;
    }, [page, totalPages]);
    const thSx = {
        px: '10px',
        py: '8px',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.05em',
        color: colorTokens.darkGrey,
        border: 'none',
        whiteSpace: 'nowrap',
        userSelect: 'none',
        textAlign: 'left',
    };
    const tdSx = {
        px: '10px',
        py: '6px',
        fontSize: 12,
        color: '#1a2233',
        border: 'none',
        whiteSpace: 'nowrap',
    };
    return (_jsxs(Box, { sx: {
            borderRadius: '16px',
            bgcolor: 'white',
            border: `1px solid ${colorTokens.borderLight}`,
            display: 'flex',
            flexDirection: 'column',
        }, children: [_jsx(Box, { sx: {
                    overflowX: 'auto',
                    overflowY: 'auto',
                    maxHeight: 'calc(100vh - 210px)',
                    /* Thin scrollbar — visible on hover/scroll only */
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'transparent transparent',
                    '&:hover': { scrollbarColor: 'rgba(0,0,0,0.18) transparent' },
                    '&::-webkit-scrollbar': { width: '4px', height: '4px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '4px' },
                    '&:hover::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.18)' },
                }, children: _jsxs(Box, { component: "table", sx: {
                        tableLayout: 'fixed',
                        width: `${totalW}px`,
                        minWidth: '100%',
                        borderCollapse: 'collapse',
                    }, children: [_jsxs("colgroup", { children: [_jsx("col", { style: { width: NAME_W } }), cols.map((c, i) => (_jsx("col", { style: { width: c.w } }, i))), _jsx("col", { style: { width: ACTIONS_W } })] }), _jsx(Box, { component: "thead", sx: { position: 'sticky', top: 0, zIndex: 10, bgcolor: colorTokens.lightGrey }, children: _jsxs(Box, { component: "tr", sx: { borderBottom: `1px solid ${colorTokens.borderLight}` }, children: [_jsx(Box, { component: "th", sx: {
                                            ...thSx,
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 21,
                                            borderRight: `1px solid ${colorTokens.borderLight}`,
                                            bgcolor: colorTokens.lightGrey,
                                            textAlign: 'left',
                                        }, children: t('table.columnPondName') }), cols.map((c) => (_jsx(Box, { component: "th", sx: { ...thSx }, children: t(c.labelKey) }, c.key))), _jsx(Box, { component: "th", sx: {
                                            ...thSx,
                                            position: 'sticky',
                                            right: 0,
                                            zIndex: 21,
                                            bgcolor: colorTokens.lightGrey,
                                            width: ACTIONS_W,
                                            textAlign: 'center',
                                        }, children: "Actions" })] }) }), _jsx("tbody", { children: ponds.length === 0 ? (_jsx("tr", { children: _jsx("td", { colSpan: cols.length + 2, children: _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }, children: _jsx(Typography, { sx: { fontSize: 12, color: colorTokens.darkGrey, mt: 1 }, children: t('table.emptyState') }) }) }) })) : (pageRows.map((pond, ri) => {
                                const notLast = ri < pageRows.length - 1;
                                // const isDeleted = pond.status === 'deleted'
                                return (_jsxs(Box, { component: "tr", sx: {
                                        borderBottom: notLast ? `1px solid ${colorTokens.borderLight}` : 'none',
                                        bgcolor: 'white',
                                        cursor: 'default',
                                        '&:hover': { bgcolor: colorTokens.primaryHoverBg },
                                    }, children: [_jsx(Box, { component: "td", sx: {
                                                ...tdSx,
                                                position: 'sticky',
                                                left: 0,
                                                bgcolor: 'inherit',
                                                zIndex: 5,
                                                borderRight: `1px solid ${colorTokens.borderLight}`,
                                            }, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(Typography, { sx: {
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            color: colorTokens.primary,
                                                            textDecoration: 'none',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }, children: pond.name }), pond.status === 'inactive' && (_jsx(Box, { sx: {
                                                            px: 0.75,
                                                            py: 0.25,
                                                            borderRadius: 1,
                                                            bgcolor: `${colorTokens.darkGrey}15`,
                                                            color: colorTokens.darkGrey,
                                                            fontSize: 9,
                                                            fontWeight: 700,
                                                            flexShrink: 0,
                                                        }, children: t('status.badgeInactive') })), pond.status === 'deleted' && (_jsx(Box, { sx: {
                                                            px: 0.75,
                                                            py: 0.25,
                                                            borderRadius: 1,
                                                            bgcolor: `${colorTokens.error}12`,
                                                            color: colorTokens.error,
                                                            fontSize: 9,
                                                            fontWeight: 700,
                                                            flexShrink: 0,
                                                        }, children: t('status.badgeDeleted') }))] }) }), cols.map((col) => (_jsx(Box, { component: "td", sx: { ...tdSx }, children: getCellValue(col.key, pond) }, col.key))), _jsx(Box, { component: "td", className: "row-actions", onClick: (e) => e.stopPropagation(), sx: {
                                                ...tdSx,
                                                position: 'sticky',
                                                right: 0,
                                                bgcolor: 'inherit',
                                                zIndex: 5,
                                                borderLeft: `1px solid ${colorTokens.borderLight}`,
                                            }, children: _jsxs(Box, { className: "pond-row-actions", sx: {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 0.5,
                                                }, children: [lifecycle === 'Active' && (_jsxs(_Fragment, { children: [onEdit && (_jsx(Box, { component: "button", onClick: () => onEdit(pond), title: t('actions.editTooltip'), sx: {
                                                                    p: 0.75,
                                                                    border: 'none',
                                                                    bgcolor: 'transparent',
                                                                    cursor: 'pointer',
                                                                    borderRadius: 1,
                                                                    display: 'flex',
                                                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
                                                                }, children: _jsxs("svg", { width: "13", height: "13", viewBox: "0 0 24 24", fill: "none", stroke: colorTokens.primary, strokeWidth: "2.2", strokeLinecap: "round", children: [_jsx("path", { d: "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" }), _jsx("path", { d: "M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" })] }) })), onDelete && (_jsx(Box, { component: "button", onClick: () => onDelete(pond), title: t('actions.deleteTooltip'), sx: {
                                                                    p: 0.75,
                                                                    border: 'none',
                                                                    bgcolor: 'transparent',
                                                                    cursor: 'pointer',
                                                                    borderRadius: 1,
                                                                    display: 'flex',
                                                                    '&:hover': { bgcolor: `${colorTokens.error}10` },
                                                                }, children: _jsx("svg", { width: "13", height: "13", viewBox: "0 0 24 24", fill: "none", stroke: colorTokens.error, strokeWidth: "2.2", strokeLinecap: "round", children: _jsx("path", { d: "M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" }) }) }))] })), lifecycle === 'Inactive' && (_jsxs(_Fragment, { children: [onActivate && (_jsx(Box, { component: "button", onClick: () => onActivate(pond), title: t('actions.activateTooltip'), sx: {
                                                                    p: 0.75,
                                                                    border: 'none',
                                                                    bgcolor: 'transparent',
                                                                    cursor: 'pointer',
                                                                    borderRadius: 1,
                                                                    display: 'flex',
                                                                    '&:hover': { bgcolor: `${colorTokens.green}10` },
                                                                }, children: _jsxs("svg", { width: "13", height: "13", viewBox: "0 0 24 24", fill: "none", stroke: colorTokens.green, strokeWidth: "2.2", strokeLinecap: "round", children: [_jsx("path", { d: "M22 11.08V12a10 10 0 1 1-5.93-9.14" }), _jsx("polyline", { points: "22 4 12 14.01 9 11.01" })] }) })), onDelete && (_jsx(Box, { component: "button", onClick: () => onDelete(pond), title: t('actions.deleteTooltip'), sx: {
                                                                    p: 0.75,
                                                                    border: 'none',
                                                                    bgcolor: 'transparent',
                                                                    cursor: 'pointer',
                                                                    borderRadius: 1,
                                                                    display: 'flex',
                                                                    '&:hover': { bgcolor: `${colorTokens.error}10` },
                                                                }, children: _jsx("svg", { width: "13", height: "13", viewBox: "0 0 24 24", fill: "none", stroke: colorTokens.error, strokeWidth: "2.2", strokeLinecap: "round", children: _jsx("path", { d: "M3 6h18M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2" }) }) }))] })), lifecycle === 'Deleted' && onRestore && (_jsx(Box, { component: "button", onClick: () => onRestore(pond), title: t('actions.restoreTooltip'), sx: {
                                                            p: 0.75,
                                                            border: 'none',
                                                            bgcolor: 'transparent',
                                                            cursor: 'pointer',
                                                            borderRadius: 1,
                                                            display: 'flex',
                                                            '&:hover': { bgcolor: `${colorTokens.primary}12` },
                                                        }, children: _jsxs("svg", { width: "13", height: "13", viewBox: "0 0 24 24", fill: "none", stroke: colorTokens.primary, strokeWidth: "2.2", strokeLinecap: "round", children: [_jsx("path", { d: "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" }), _jsx("path", { d: "M3 3v5h5" })] }) }))] }) })] }, pond.id));
                            })) }), ponds.length > 0 && (_jsx(Box, { component: "tfoot", sx: { position: 'sticky', bottom: 0, zIndex: 10 }, children: _jsxs(Box, { component: "tr", sx: {
                                    borderTop: `2px solid ${colorTokens.borderLight}`,
                                    bgcolor: colorTokens.surfaceAlt,
                                }, children: [_jsxs(Box, { component: "td", sx: {
                                            ...tdSx,
                                            position: 'sticky',
                                            left: 0,
                                            bgcolor: colorTokens.surfaceAlt,
                                            zIndex: 11,
                                            borderRight: `1px solid ${colorTokens.borderLight}`,
                                        }, children: [_jsx(Typography, { component: "span", sx: {
                                                    fontSize: 10,
                                                    fontWeight: 800,
                                                    color: colorTokens.primary,
                                                    letterSpacing: '0.04em',
                                                    textTransform: 'uppercase',
                                                }, children: "Farm Total" }), _jsxs(Typography, { component: "span", sx: { fontSize: 9, color: colorTokens.darkGrey, ml: 0.5 }, children: ["(", ponds.length, ")"] })] }), cols.map((c) => {
                                        const entry = colTotals[c.key];
                                        return (_jsx(Box, { component: "td", sx: { ...tdSx }, children: entry == null ? (_jsx(Typography, { component: "span", sx: { color: colorTokens.gray300 }, children: "\u2014" })) : (_jsxs(Typography, { component: "span", sx: { fontWeight: 700, color: colorTokens.textDark, fontSize: 12 }, children: [formatTotal(c.key, entry.value), _jsx(Typography, { component: "span", sx: {
                                                            fontSize: 9,
                                                            color: colorTokens.darkGrey,
                                                            fontWeight: 400,
                                                            ml: 0.25,
                                                        }, children: entry.isAvg ? 'avg' : 'total' })] })) }, c.key));
                                    }), _jsx(Box, { component: "td", sx: {
                                            ...tdSx,
                                            position: 'sticky',
                                            right: 0,
                                            bgcolor: colorTokens.surfaceAlt,
                                            zIndex: 11,
                                            borderLeft: `1px solid ${colorTokens.borderLight}`,
                                        } })] }) }))] }) }), ponds.length > 0 && (_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    px: 2,
                    py: 1,
                    borderTop: `1px solid ${colorTokens.borderLight}`,
                    bgcolor: colorTokens.lightGrey,
                    gap: 1.5,
                    borderRadius: '0 0 16px 16px',
                }, children: [_jsx(Box, { component: "select", value: perPage, onChange: (e) => setPerPageSafe(Number(e.target.value)), sx: {
                            fontSize: 11,
                            fontWeight: 600,
                            color: colorTokens.textDark,
                            border: `1px solid ${colorTokens.borderLight}`,
                            borderRadius: 1,
                            px: 1,
                            py: 0.5,
                            bgcolor: 'white',
                            cursor: 'pointer',
                            outline: 'none',
                            fontFamily: 'inherit',
                        }, children: PAGE_SIZES.map((n) => (_jsxs("option", { value: n, children: [n, " / page"] }, n))) }), _jsxs(Typography, { sx: { fontSize: 11, color: colorTokens.darkGrey, whiteSpace: 'nowrap' }, children: [(page - 1) * perPage + 1, "\u2013", Math.min(page * perPage, totalCount), " of ", totalCount] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.25 }, children: [_jsx(Box, { component: "button", onClick: () => setPageSafe(Math.max(1, page - 1)), disabled: page === 1, sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 0.5,
                                    border: 'none',
                                    bgcolor: 'transparent',
                                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                                    color: colorTokens.darkGrey,
                                    opacity: page === 1 ? 0.3 : 1,
                                    borderRadius: 1,
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                                }, children: _jsx(ChevL, {}) }), pageNums.map((p, idx) => p === '…' ? (_jsx(Typography, { sx: { fontSize: 11, color: colorTokens.darkGrey, px: 0.5 }, children: "\u2026" }, `e${idx}`)) : (_jsx(Box, { component: "button", onClick: () => setPageSafe(p), sx: {
                                    width: 26,
                                    height: 26,
                                    border: 'none',
                                    borderRadius: 1,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    bgcolor: page === p ? colorTokens.primary : 'transparent',
                                    color: page === p ? 'white' : colorTokens.darkGrey,
                                    '&:hover': {
                                        bgcolor: page === p ? colorTokens.primary : 'rgba(0,0,0,0.04)',
                                    },
                                }, children: p }, p))), _jsx(Box, { component: "button", onClick: () => setPageSafe(Math.min(totalPages, page + 1)), disabled: page === totalPages, sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 0.5,
                                    border: 'none',
                                    bgcolor: 'transparent',
                                    cursor: page === totalPages ? 'not-allowed' : 'pointer',
                                    color: colorTokens.darkGrey,
                                    opacity: page === totalPages ? 0.3 : 1,
                                    borderRadius: 1,
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                                }, children: _jsx(ChevR, {}) })] })] }))] }));
});
